import * as Chart from 'chart.js';
import { SECTION_PRESS_ADL_NAMES, SECTION_PRESS_LOCOMO_NAMES, MONTHLY_DAYS_COUNT } from '../constants/Training';

interface BindingValue {
  history_monthly_lt: (number | Chart.ChartPoint)[][];
  history_monthly_angle_lt: (number | Chart.ChartPoint)[][];
  history_monthly: (number | Chart.ChartPoint)[][];
  history_monthly_angle: (number | Chart.ChartPoint)[][];
  history_monthly_start: (number | Chart.ChartPoint)[][];
  history_monthly_angle_start: (number | Chart.ChartPoint)[][];
  trainingCount_locomo: (number | Chart.ChartPoint)[];
  trainingCount_adl: (number | Chart.ChartPoint)[];
}

export interface Binding {
  value: BindingValue;
}

export class Graph {
  public lineChart1: Chart;

  // コンストラクタ.
  constructor() {
    if (!(this instanceof Graph)) {
      return new Graph();
    }
  }

  // グラフ更新(Chart.js)
  // http://chartjs.org/
  // ロコモorADL・詳細・比較用グラフ.
  insertDataCanvasGraph = (
    canvasElement: HTMLCanvasElement, binding: Binding, menuNumber: number, width: number,
    height: number, yAxis1Max: number, yAxis2Max: number, isStartTerm: boolean = false) => {

    // Get canvas context
    const context1 = canvasElement.getContext('2d');
    if (context1 === null) {
      return;
    }

    context1.canvas.width = width;
    context1.canvas.height = height;

    let dailyIndex = new Array(MONTHLY_DAYS_COUNT).fill('');
    for (let i: number = 0; i < MONTHLY_DAYS_COUNT; i++) {
      dailyIndex[i] = `${i.toString()}日`;
    }

    let historyCount: (number | Chart.ChartPoint)[] = [];
    let historyAngle: (number | Chart.ChartPoint)[]  = [];
    if (isStartTerm) {
      historyCount = binding.value.history_monthly_start[menuNumber];
      historyAngle = binding.value.history_monthly_angle_start[menuNumber];
    } else {
      historyCount = binding.value.history_monthly[menuNumber];
      historyAngle = binding.value.history_monthly_angle[menuNumber];
    }

    // グラフのデータのあるplotだけに限定つなぐようにする.
    let dailyIndexNew: (number | Chart.ChartPoint | string)[] = [];
    let historyCountNew: (number | Chart.ChartPoint)[] = [];
    if (historyCount !== undefined) {
      for (let i: number = 0; i < historyCount.length; i++) {
        if (historyCount[i]) {
          dailyIndexNew.push(`${i}日`);
          historyCountNew.push(historyCount[i]);
        }
      }
    }

    const historyAngleNew = [];
    if (historyAngle !== undefined) {
      for (let i: number = 0; i < historyAngle.length; i++) {
        if (historyAngle[i]) {
          historyAngleNew.push(historyAngle[i]);
        }
      }
    }

    dailyIndex = dailyIndexNew;
    historyCount = historyCountNew;
    historyAngle = historyAngleNew;

    const lineData: Chart.ChartData = {
      labels: dailyIndex, // X軸のラベル
      datasets: [
        {
          label: '回数', // 項目名
          yAxisID: 'y-axis-1', // 軸
          fill: true,
          lineTension: 0,
          backgroundColor: 'rgba(210,230,230,0.5)',
          borderColor: 'rgba(50,150,150,1)',
          data: historyCount
        },
        {
          label: '角度', // 項目名
          yAxisID: 'y-axis-2', // 軸
          fill: true,
          lineTension: 0,
          backgroundColor: 'rgba(230,200,190,0.5)',
          borderColor: 'rgba(160,100,40,1)',
          data: historyAngle
        }
      ] as Chart.ChartDataSets[]
    };

    const chartConfig: Chart.ChartConfiguration = {
      type: 'line',
      data: lineData,
      options: {
        responsive: false,
        scales: {
        yAxes: [{
          id: 'y-axis-1',
          type: 'linear',
          position: 'left',
          ticks: { // スケール
            max: yAxis1Max,
            min: 0,
            stepSize: 10
          } as Chart.LinearTickOptions,
          scaleLabel: {
            display: true,
            labelString: '回数',
            fontSize:  10
          },
        }, {
          id:  'y-axis-2',
          type:  'linear',
          position:  'right',
          ticks: {
            max: yAxis2Max,
            min:  0,
            stepSize: 10
          } as Chart.LinearTickOptions,
          scaleLabel: {
            display:  true,
            labelString:  '角度',
            fontSize:  10
          },
          gridLines: { // このオプションを追加
            drawOnChartArea:  false,
          },
        }],
        }
      }
    };

    this.lineChart1 = new Chart(context1, chartConfig); // end of Chart
  }

  // ロコモorADL・長期変化用グラフ.
  insertDataLongtermCanvasGraph = (
    canvasElement: HTMLCanvasElement, binding: Binding, menuNumber: number, width: number,
    height: number, yAxis1Max: number, yAxis2Max: number) => {

    // Get canvas context
    const context1 = canvasElement.getContext('2d');
    if (context1 === null) {
      return;
    }

    context1.canvas.width = width;
    context1.canvas.height = height;

    let historyCount = binding.value.history_monthly_lt[menuNumber];
    let historyAngle = binding.value.history_monthly_angle_lt[menuNumber];

    // グラフのデータのあるplotだけに限定つなぐようにする.
    const dailyIndex: string[] = [];
    if (historyCount !== undefined) {
      const historyCountNew = [];
      for (let key in historyCount) {
        if (historyCount[key]) {
          const dateStr = key.substr(5);
          dailyIndex.push(dateStr);
          historyCountNew.push(historyCount[key]);
        }
      }

      historyCount = historyCountNew;
    }

    if (historyAngle !== undefined) {
      const historyAngleNew = [];

      for (let key in historyAngle) {
        if (historyAngle[key]) {
          historyAngleNew.push(historyAngle[key]);
        }
      }
  
      historyAngle = historyAngleNew;
    }

    const lineData: Chart.ChartData = {
      labels: dailyIndex, // X軸のラベル
      datasets: [
        {
          label: '回数', // 項目名
          yAxisID: 'y-axis-1', // 軸
          fill: true,
          lineTension: 0,
          backgroundColor: 'rgba(210,230,230,0.5)',
          borderColor: 'rgba(50,150,150,1)',
          data: historyCount
        },
        {
          label: '角度', // 項目名
          yAxisID: 'y-axis-2', // 軸
          fill: true,
          lineTension: 0,
          backgroundColor: 'rgba(230,200,190,0.5)',
          borderColor: 'rgba(160,100,40,1)',
          data: historyAngle
        }
      ] as Chart.ChartDataSets[]
    };

    this.lineChart1 = new Chart(context1, {
      type: 'line',
      data: lineData,
      options: {
        events: ['click'],
        responsive: false,
        scales: {
          yAxes: [{
            id: 'y-axis-1', // Y軸のID
            type: 'linear', // linear固定
            position: 'left', // どちら側に表示される軸か？
            ticks: { // スケール
              max: yAxis1Max,
              min: 0,
              stepSize: 10
            } as Chart.LinearTickOptions,
            scaleLabel: {
              display: true,
              labelString: '回数',
              fontSize: 10
            },
          }, {
            id: 'y-axis-2',
            type: 'linear',
            position: 'right',
            ticks: {
              max: yAxis2Max,
              min: 0,
              stepSize: 10
            } as Chart.LinearTickOptions,
            scaleLabel: {
              display: true,
              labelString: '角度',
              fontSize: 10
            },
            gridLines: {
              drawOnChartArea: false,
            },
          }],
        }
      }
    }); // end of Chart
  }

  /**
   * モフトレ通信用ロコモ予防トレーニングの円グラフの作成.
   *
   * @memberof Graph
   */
  insertDataCanvasPressPieChartLocomo = (
    canvasElement: HTMLCanvasElement, binding: Binding, menuNumber: number, width: number,
    height: number, yAxis1Max: number, yAxis2Max: number) => {

    // Get canvas context
    const context1 = canvasElement.getContext('2d');
    if (context1 === null) {
      return;
    }

    context1.canvas.width = width;
    context1.canvas.height = height;

    // [0,0,0,0,0,0,0].
    const trainingCount = binding.value.trainingCount_locomo;

    const data: Chart.ChartData = {
      labels: SECTION_PRESS_LOCOMO_NAMES,
      datasets: [{
        data: trainingCount,
        // 円グラフの各色.
        backgroundColor: [
          'rgba(100,147,225,1.0)',
          'rgba(229,142,191,1.0)',
          'rgba( 99,207,147,1.0)',
          'rgba(220,113,100,1.0)',
          'rgba(161,111,198,1.0)',
          'rgba(237,205,105,1.0)',
          'rgba(221,142, 87,1.0)',
          // 上部腹筋.
          'rgba(  0,116,191,1.0)',
          // ローイング.
          'rgba( 86,167,100,1.0)',
        ],
      }],
    } as Chart.ChartData;

    const options: ChartOptions = {
      responsive: true,
      legend: false,
    } as ChartOptions;

    this.lineChart1 = new Chart(context1, {
      type: 'pie',
      data: data,
      options: options
    } as Chart.ChartConfiguration);
  }

  // ADL用円グラフの作成.
  insertDataCanvasPressPieChartAdl = (
    canvasElement: HTMLCanvasElement, binding: Binding, menuNumber: number, width: number,
    height: number, yAxis1Max: number, yAxis2Max: number) => {

    // Get canvas context
    const context1 = canvasElement.getContext('2d');
    if (context1 === null) {
      return;
    }

    context1.canvas.width = width;
    context1.canvas.height = height;

    // [0,0,0,0,0,0,0].
    const trainingCount = binding.value.trainingCount_adl;

    const data: Chart.ChartData = {
      labels: SECTION_PRESS_ADL_NAMES,
      datasets: [{
        data: trainingCount,
        // 円グラフの各色.
        backgroundColor: [
          'rgba(229,142,191,1.0)',
          'rgba(100,147,225,1.0)',
          'rgba( 99,207,147,1.0)',
          'rgba(161,111,198,1.0)',
          'rgba(220,113,100,1.0)', // 未使用(更衣着脱衣(上半身)).
          'rgba(221,142, 87,1.0)', // 未使用(更衣着脱衣(靴下)).
          'rgba(237,205,105,1.0)',
        ],
      }],
    } as Chart.ChartData;

    const options: ChartOptions = {
      responsive: true,
      legend: false,
    } as ChartOptions;

    this.lineChart1 = new Chart(context1, {
      type: 'pie',
      data: data,
      options: options
    } as Chart.ChartConfiguration);
  }
}

/**
 * ロコモのグラフのY軸角度の最大数を取得.
 *
 * @param {number} menuNumber 0開始の項目数(トレーニングID - 1).
 * @returns {number}
 */
export const getLocomoAxis2Max = (menuNumber: number): number => {
  let yAxis2Max = 180;
  // グラフの角度.
  if (menuNumber === 0 || menuNumber === 1) {
    // 腕上げ下ろし, 腕左右.
    yAxis2Max = 180;
  } else if (menuNumber >= 2 && menuNumber < 7) {
    // ひざのばし, ももあげ, 股開閉, よこあげ, おしり.
    yAxis2Max = 100;
  } else if (menuNumber === 7) {
    // 上部腹筋.
    yAxis2Max = 90;
  } else if (menuNumber === 8) {
    // ローイング.
    yAxis2Max = 110;
  }

  return yAxis2Max;
};


// WEBPACK FOOTER //
// ./src/utils/Graph.tsx